if (typeof B2bStickyBar !== 'function') {
  class B2bStickyBar extends HTMLElement {
    #button = null;
    #targetSection = null;
    #observer = null;
    #hasScrolled = false;
    #isSectionVisible = true;
    #isBarVisible = false;
    #hideTimeout = null;
    #onScroll = null;
    #onClick = null;
    #onHideTransitionEnd = null;

    connectedCallback() {
      this.#button = this.querySelector('.b2b-sticky-bar__button');
      this.#targetSection = document.getElementById(this.dataset.sectionId);

      if (!this.#button || !this.#targetSection) {
        return;
      }

      this.#onScroll = () => {
        this.#hasScrolled = window.scrollY > 0;
        this.#updateScrollDirection();
        this.#updateVisibility();
      };

      this.#onClick = () => {
        const offset = this.#getStickyHeaderOffset();
        const top = this.#targetSection.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({ top, behavior: 'smooth' });
      };

      this.#onHideTransitionEnd = (event) => {
        if (event.target !== this || event.propertyName !== 'transform') {
          return;
        }

        if (!this.#isBarVisible) {
          this.#setHidden(true);
        }
      };

      window.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#button.addEventListener('click', this.#onClick);
      this.addEventListener('transitionend', this.#onHideTransitionEnd);

      this.#observer = new IntersectionObserver(
        (entries) => {
          this.#isSectionVisible = entries[0]?.isIntersecting ?? true;
          this.#updateScrollDirection();
          this.#updateVisibility();
        },
        {
          threshold: 0,
          rootMargin: `-${this.#getStickyHeaderOffset()}px 0px 0px 0px`,
        }
      );

      this.#observer.observe(this.#targetSection);
      this.#updateScrollDirection();
      this.#updateVisibility();
    }

    disconnectedCallback() {
      if (this.#onScroll) {
        window.removeEventListener('scroll', this.#onScroll);
      }

      if (this.#button && this.#onClick) {
        this.#button.removeEventListener('click', this.#onClick);
      }

      this.removeEventListener('transitionend', this.#onHideTransitionEnd);
      this.#clearHideTimeout();
      this.#observer?.disconnect();
    }

    #getStickyHeaderOffset() {
      const styles = getComputedStyle(document.documentElement);

      return (
        parseInt(styles.getPropertyValue('--header-height') || 0, 10) *
          parseInt(styles.getPropertyValue('--enable-sticky-header') || 0, 10) +
        parseInt(styles.getPropertyValue('--announcement-bar-height') || 0, 10) *
          parseInt(styles.getPropertyValue('--enable-sticky-announcement-bar') || 0, 10)
      );
    }

    #clearHideTimeout() {
      if (this.#hideTimeout) {
        window.clearTimeout(this.#hideTimeout);
        this.#hideTimeout = null;
      }
    }

    #setHidden(hidden) {
      this.#clearHideTimeout();

      if (hidden) {
        this.setAttribute('hidden', '');
        this.classList.remove('is-instant');

        return;
      }

      this.removeAttribute('hidden');
    }

    #updateScrollDirection() {
      const rect = this.#targetSection.getBoundingClientRect();
      const offset = this.#getStickyHeaderOffset();
      const isSectionBelow = rect.top > offset;

      this.classList.toggle('b2b-sticky-bar--section-below', isSectionBelow);
      this.classList.toggle('b2b-sticky-bar--section-above', !isSectionBelow);
    }

    #updateVisibility() {
      const isAtPageTop = window.scrollY <= 0;
      const shouldShow = this.#hasScrolled && !this.#isSectionVisible && !isAtPageTop;

      if (shouldShow) {
        this.#clearHideTimeout();
        this.classList.remove('is-instant');

        if (this.#isBarVisible) {
          return;
        }

        this.#isBarVisible = true;
        this.#setHidden(false);
        this.setAttribute('aria-hidden', 'false');

        requestAnimationFrame(() => {
          this.classList.add('is-visible');
        });

        return;
      }

      if (!this.#isBarVisible) {
        this.#setHidden(true);

        return;
      }

      this.#isBarVisible = false;
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');

      if (isAtPageTop) {
        this.classList.add('is-instant');
        this.#setHidden(true);

        return;
      }

      this.#hideTimeout = window.setTimeout(() => {
        if (!this.#isBarVisible) {
          this.#setHidden(true);
        }
      }, 400);
    }
  }

  if (!customElements.get('b2b-sticky-bar')) {
    customElements.define('b2b-sticky-bar', B2bStickyBar);
  }
}
